import { toast } from "sonner";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  createDrug as createDrugService,
  deleteDrug as deleteDrugService,
  getDrugsByFilter as getDrugsByFilterService,
  updateDrug as updateDrugService,
  getDrugsStockByFilter as getDrugsStockByFilterService,
  createDrugStock as createDrugStockService,
} from "../../services/drugs";
import * as types from "./types";
import { adaptResponseError } from "../../utils/utils";

/**
 * Sagas du domaine Drugs (Phase 2.F).
 *
 * <p>Conventions Phase 2.D :
 * <ul>
 *   <li>{@code call(service, ...)} pour testabilité.</li>
 *   <li>Lecture de {@code result.ok} (plus de {@code result.success}).</li>
 *   <li>POST → Long → refetch : on dispatche le refresh de la liste après
 *       un succès, sans tenter de lire le DTO complet sur la réponse.</li>
 *   <li>Pas de token mémorisé au chargement du module — apiRequest gère
 *       l'auth via le storage.</li>
 * </ul>
 *
 * <p>Les services {@code getDrugsByFilter} / {@code getDrugsStockByFilter}
 * sont des alias rétro-compatibles qui aplatissent la réponse paginée en
 * tableau plat — c'est ce qui est stocké dans {@code drugs} /
 * {@code drugsSuivie} pour préserver l'API des consommateurs (Pharmacy,
 * Consultation/TabTwo).
 */

function* createDrug({ payload }) {
  try {
    const result = yield call(createDrugService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_DRUG_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Médicament créé avec succès");
    yield put({ type: types.CREATE_DRUG_SUCCESS, payload: { id: result.data } });
    yield put({ type: types.GET_DRUGS_BY_FILTER_REQUEST, payload: {} });
  } catch (error) {
    console.error("CreateDrugSagaErr: ", error);
    yield put({
      type: types.CREATE_DRUG_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* createDrugStock({ payload }) {
  try {
    const result = yield call(createDrugStockService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_DRUG_STOCK_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Mouvement de stock enregistré");
    yield put({
      type: types.CREATE_DRUG_STOCK_SUCCESS,
      payload: { id: result.data },
    });
    yield put({ type: types.GET_DRUGS_STOCK_BY_FILTER_REQUEST, payload: {} });
  } catch (error) {
    console.error("CreateDrugStockSagaErr: ", error);
    yield put({
      type: types.CREATE_DRUG_STOCK_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* getDrugsByFilter({ payload }) {
  try {
    const result = yield call(getDrugsByFilterService, payload || {});
    if (!result?.ok) {
      yield put({ type: types.GET_DRUGS_BY_FILTER_FAILURE, payload: [] });
      return;
    }
    yield put({
      type: types.GET_DRUGS_BY_FILTER_SUCCESS,
      payload: result?.data || [],
    });
  } catch (error) {
    console.error("GetDrugsByFilterSagaErr: ", error);
    yield put({ type: types.GET_DRUGS_BY_FILTER_FAILURE, payload: [] });
  }
}

function* getDrugsStockByFilter({ payload }) {
  try {
    const result = yield call(getDrugsStockByFilterService, payload || {});
    if (!result?.ok) {
      yield put({ type: types.GET_DRUGS_STOCK_BY_FILTER_FAILURE, payload: [] });
      return;
    }
    yield put({
      type: types.GET_DRUGS_STOCK_BY_FILTER_SUCCESS,
      payload: result?.data || [],
    });
  } catch (error) {
    console.error("GetDrugsStockByFilterSagaErr: ", error);
    yield put({ type: types.GET_DRUGS_STOCK_BY_FILTER_FAILURE, payload: [] });
  }
}

function* updateDrug({ payload }) {
  try {
    const result = yield call(updateDrugService, payload);
    if (!result?.ok) {
      yield put({
        type: types.UPDATE_DRUG_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Médicament mis à jour");
    yield put({ type: types.UPDATE_DRUG_SUCCESS, payload: { id: result.data } });
    yield put({ type: types.GET_DRUGS_BY_FILTER_REQUEST, payload: {} });
  } catch (error) {
    console.error("UpdateDrugSagaErr: ", error);
    yield put({
      type: types.UPDATE_DRUG_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* deleteDrug({ payload }) {
  try {
    const result = yield call(deleteDrugService, payload);
    if (!result?.ok) {
      yield put({ type: types.DELETE_DRUG_FAILURE, payload: [] });
      return;
    }
    toast.success("Médicament supprimé");
    const id =
      typeof payload === "object" && payload !== null ? payload?.id : payload;
    yield put({ type: types.DELETE_DRUG_SUCCESS, payload: { id } });
    yield put({ type: types.GET_DRUGS_BY_FILTER_REQUEST, payload: {} });
  } catch (error) {
    console.error("DeleteDrugSagaErr: ", error);
    yield put({ type: types.DELETE_DRUG_FAILURE, payload: [] });
  }
}

export default function* DrugsSaga() {
  yield takeLatest(types.CREATE_DRUG_REQUEST, createDrug);
  yield takeLatest(types.GET_DRUGS_BY_FILTER_REQUEST, getDrugsByFilter);
  yield takeLatest(types.GET_DRUGS_STOCK_BY_FILTER_REQUEST, getDrugsStockByFilter);
  yield takeLatest(types.UPDATE_DRUG_REQUEST, updateDrug);
  yield takeLatest(types.DELETE_DRUG_REQUEST, deleteDrug);
  yield takeLatest(types.CREATE_DRUG_STOCK_REQUEST, createDrugStock);
}
